import { RoomEvent, Track } from 'livekit-client';
import type { RemoteParticipant, RemoteTrackPublication, Room } from 'livekit-client';

/**
 * Watchdog de áudio remoto — detecta "não estou ouvindo o X" e recupera sozinho.
 *
 * Relatos (set/2026): um participante deixa de ouvir *um* colega (ouve os demais), no
 * celular ou após ~1 h de sala. Causas típicas no subscriber:
 *  - publicação existe mas a inscrição não foi (re)feita após reconexão de sinal
 *    (`isSubscribed=false` com `isDesired=true`);
 *  - track inscrita mas nunca anexada a um `<audio>` (evento perdido pelo renderer);
 *  - inscrição ativa porém sem bytes chegando enquanto o servidor marca o colega como
 *    "falando" (active speaker vem do publisher, independente do nosso receiver).
 *
 * A cada tick (5 s) amostramos essas 3 condições por publicação de microfone remoto e,
 * após `staleTicks` amostras seguidas ruins (~10 s), re-inscrevemos a track
 * (`setSubscribed(false)` → `setSubscribed(true)`), com cooldown por publicação para
 * não entrar em loop. Só atua em áudio; nunca toca na câmera/tela.
 */

export const REMOTE_AUDIO_WATCHDOG_INTERVAL_MS = 5_000;
export const REMOTE_AUDIO_WATCHDOG_STALE_TICKS = 2;
export const REMOTE_AUDIO_WATCHDOG_COOLDOWN_MS = 30_000;
/** Pausa entre unsubscribe e subscribe para o SFU processar a atualização. */
export const REMOTE_AUDIO_WATCHDOG_RESUBSCRIBE_GAP_MS = 400;

export type RemoteAudioWatchdogReason =
  | 'not-subscribed'
  | 'not-attached'
  | 'no-bytes-while-speaking';

export interface RemoteAudioWatchdogRecovery {
  participantIdentity: string;
  trackSid: string;
  reason: RemoteAudioWatchdogReason;
}

export interface RemoteAudioWatchdogOptions {
  intervalMs?: number;
  staleTicks?: number;
  cooldownMs?: number;
  resubscribeGapMs?: number;
  onRecover?: (info: RemoteAudioWatchdogRecovery) => void;
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
}

interface PublicationState {
  staleTicks: number;
  lastBytes: number | undefined;
  lastRecoverAt: number;
  recovering: boolean;
}

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

function isRemoteMicPublication(pub: RemoteTrackPublication): boolean {
  if (pub.kind !== Track.Kind.Audio) return false;
  // Áudio de tela também importa para o ouvinte, mas o sintoma relatado é voz.
  return pub.source === Track.Source.Microphone || pub.source === Track.Source.Unknown;
}

async function readBytesReceived(pub: RemoteTrackPublication): Promise<number | undefined> {
  const track = pub.audioTrack as
    | { getReceiverStats?: () => Promise<{ bytesReceived?: number } | undefined> }
    | undefined;
  if (!track || typeof track.getReceiverStats !== 'function') return undefined;
  try {
    const stats = await track.getReceiverStats();
    return stats?.bytesReceived;
  } catch {
    return undefined;
  }
}

/**
 * Avalia uma publicação. Devolve o motivo se ela estiver "ruim" neste tick, ou `null`.
 * Exportado para testes.
 */
export async function evaluateRemoteAudioPublication(
  participant: Pick<RemoteParticipant, 'isSpeaking' | 'audioLevel'>,
  pub: RemoteTrackPublication,
  state: PublicationState,
): Promise<RemoteAudioWatchdogReason | null> {
  if (pub.isMuted) {
    state.lastBytes = undefined;
    return null;
  }

  if (!pub.isSubscribed) {
    // Explicitamente desinscrita pela app (isDesired=false) não é falha.
    return pub.isDesired === false ? null : 'not-subscribed';
  }

  const track = pub.audioTrack;
  if (!track) return 'not-subscribed';

  if (track.attachedElements.length === 0) {
    return 'not-attached';
  }

  const bytes = await readBytesReceived(pub);
  const prev = state.lastBytes;
  state.lastBytes = bytes;
  if (bytes === undefined || prev === undefined) return null;

  const speaking = participant.isSpeaking || (participant.audioLevel ?? 0) > 0;
  if (speaking && bytes <= prev) {
    return 'no-bytes-while-speaking';
  }
  return null;
}

export class RemoteAudioWatchdog {
  private readonly states = new Map<string, PublicationState>();
  private timer: ReturnType<typeof setInterval> | undefined;
  private readonly intervalMs: number;
  private readonly staleTicks: number;
  private readonly cooldownMs: number;
  private readonly resubscribeGapMs: number;
  private readonly now: () => number;
  private readonly sleep: (ms: number) => Promise<void>;
  private readonly onRecover?: (info: RemoteAudioWatchdogRecovery) => void;
  private ticking = false;

  constructor(
    private readonly room: Room,
    opts: RemoteAudioWatchdogOptions = {},
  ) {
    this.intervalMs = opts.intervalMs ?? REMOTE_AUDIO_WATCHDOG_INTERVAL_MS;
    this.staleTicks = opts.staleTicks ?? REMOTE_AUDIO_WATCHDOG_STALE_TICKS;
    this.cooldownMs = opts.cooldownMs ?? REMOTE_AUDIO_WATCHDOG_COOLDOWN_MS;
    this.resubscribeGapMs = opts.resubscribeGapMs ?? REMOTE_AUDIO_WATCHDOG_RESUBSCRIBE_GAP_MS;
    this.now = opts.now ?? (() => Date.now());
    this.sleep = opts.sleep ?? defaultSleep;
    this.onRecover = opts.onRecover;
  }

  start(): void {
    if (this.timer) return;
    this.timer = setInterval(() => {
      void this.tick();
    }, this.intervalMs);
    this.room.on(RoomEvent.Reconnected, this.onReconnected);
    this.room.on(RoomEvent.TrackUnpublished, this.onTrackUnpublished);
  }

  stop(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = undefined;
    }
    this.room.off(RoomEvent.Reconnected, this.onReconnected);
    this.room.off(RoomEvent.TrackUnpublished, this.onTrackUnpublished);
    this.states.clear();
  }

  /** Após reconexão o receiver reinicia contadores — zera o histórico para não gerar falso positivo. */
  private onReconnected = () => {
    for (const st of this.states.values()) {
      st.staleTicks = 0;
      st.lastBytes = undefined;
    }
  };

  private onTrackUnpublished = (pub: RemoteTrackPublication) => {
    this.states.delete(pub.trackSid);
  };

  /** Um ciclo de verificação. Exportado (público) para testes determinísticos. */
  async tick(): Promise<void> {
    if (this.ticking) return;
    if (this.room.state !== 'connected') return;
    this.ticking = true;
    try {
      const seen = new Set<string>();
      for (const participant of this.room.remoteParticipants.values()) {
        for (const pub of participant.trackPublications.values()) {
          if (!isRemoteMicPublication(pub)) continue;
          seen.add(pub.trackSid);
          const state = this.stateFor(pub.trackSid);
          if (state.recovering) continue;

          const reason = await evaluateRemoteAudioPublication(participant, pub, state);
          if (!reason) {
            state.staleTicks = 0;
            continue;
          }
          state.staleTicks += 1;
          if (state.staleTicks < this.staleTicks) continue;
          if (this.now() - state.lastRecoverAt < this.cooldownMs) continue;

          void this.recover(participant, pub, state, reason);
        }
      }
      for (const sid of this.states.keys()) {
        if (!seen.has(sid)) this.states.delete(sid);
      }
    } finally {
      this.ticking = false;
    }
  }

  private stateFor(sid: string): PublicationState {
    let st = this.states.get(sid);
    if (!st) {
      st = { staleTicks: 0, lastBytes: undefined, lastRecoverAt: 0, recovering: false };
      this.states.set(sid, st);
    }
    return st;
  }

  private async recover(
    participant: RemoteParticipant,
    pub: RemoteTrackPublication,
    state: PublicationState,
    reason: RemoteAudioWatchdogReason,
  ): Promise<void> {
    state.recovering = true;
    state.staleTicks = 0;
    state.lastBytes = undefined;
    state.lastRecoverAt = this.now();
    const info: RemoteAudioWatchdogRecovery = {
      participantIdentity: participant.identity,
      trackSid: pub.trackSid,
      reason,
    };
    console.warn('[remote-audio-watchdog] re-inscrevendo áudio remoto', info);
    try {
      if (reason === 'not-subscribed') {
        pub.setSubscribed(true);
      } else {
        pub.setSubscribed(false);
        await this.sleep(this.resubscribeGapMs);
        pub.setSubscribed(true);
      }
      this.onRecover?.(info);
    } catch (e) {
      console.warn('[remote-audio-watchdog] falha ao re-inscrever', info, e);
    } finally {
      state.recovering = false;
    }
  }
}
